import { useState } from "react";
import { Check, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { MEALS, MEAL_LABEL, dayShort } from "@/lib/meals";

const FAIL = "Couldn't get ideas right now. Try again in a moment.";
const k = (s) => `${s.date}|${s.meal}`;

export default function PlanWeekDialog({ hubId, week, onClose, onSaved }) {
  const filled = new Set(week.slots.map(k));
  const [picked, setPicked] = useState(() => new Set(week.days.filter((d) => !filled.has(`${d}|dinner`)).map((d) => `${d}|dinner`)));
  const [sugg, setSugg] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const base = `/organizations/${hubId}/meals`;
  const toggle = (key) => setPicked((p) => { const n = new Set(p); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const ask = async (slots, avoid = []) => (await api.post(`${base}/plan/suggest`, { slots, avoid }, { timeout: 40000 })).data.suggestions;
  const suggest = async () => {
    setBusy("suggest"); setErr("");
    try { setSugg(await ask([...picked].map((x) => { const [date, meal] = x.split("|"); return { date, meal }; }))); } catch { setErr(FAIL); } finally { setBusy(""); }
  };
  const retry = async (s) => {
    setBusy(k(s));
    try {
      const [n] = await ask([{ date: s.date, meal: s.meal }], sugg.map((x) => x.name));
      if (n) setSugg((all) => all.map((x) => (k(x) === k(s) ? n : x)));
    } catch { toast.error(FAIL); } finally { setBusy(""); }
  };
  const accept = async (items) => {
    setBusy("accept");
    try {
      const { data } = await api.post(`${base}/plan/accept`, { items });
      toast.success(`Filled ${data.filled} slot${data.filled === 1 ? "" : "s"}`);
      const left = sugg.filter((x) => !items.some((i) => k(i) === k(x)));
      onSaved();
      if (left.length) setSugg(left); else onClose();
    } catch { toast.error(FAIL); } finally { setBusy(""); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent bigClose className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="plan-week-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display"><Sparkles className="h-5 w-5 text-[rgb(var(--glow-lists))]" /> Plan my week</DialogTitle></DialogHeader>
        {err && <p className="rounded-xl bg-[rgba(239,68,68,0.12)] px-3 py-2 text-sm font-semibold text-[rgb(var(--st-red))]" data-testid="plan-error">{err}</p>}
        {!sugg ? (
          <div className="space-y-3">
            <p className="text-sm text-q-muted">Pick the slots to fill. Ideas use your recipe box and family memory, and never include anyone's allergens.</p>
            <div className="grid grid-cols-[auto_repeat(3,minmax(0,1fr))] items-center gap-1 text-xs">
              <span />
              {MEALS.map((m) => <span key={m} className="text-center font-bold text-q-faint">{MEAL_LABEL[m]}</span>)}
              {week.days.map((d) => [
                <span key={d} className="pr-2 font-bold text-q-muted">{dayShort(d)}</span>,
                ...MEALS.map((m) => (
                  <label key={m} className="flex min-h-[44px] items-center justify-center gap-1 rounded-lg hover:bg-q-raised">
                    <Checkbox checked={picked.has(`${d}|${m}`)} onCheckedChange={() => toggle(`${d}|${m}`)} data-testid={`plan-pick-${d}-${m}`} />
                    {filled.has(`${d}|${m}`) && <span className="text-[10px] text-q-faint">filled</span>}
                  </label>
                )),
              ])}
            </div>
            <button className="q-btn q-btn-primary w-full" disabled={!picked.size || !!busy} onClick={suggest} data-testid="plan-suggest-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Suggest {picked.size} meal{picked.size === 1 ? "" : "s"}</button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="plan-suggestions">
            {sugg.map((s) => (
              <div key={k(s)} className="rounded-2xl border border-q-line bg-q-raised p-3" data-testid={`plan-suggestion-${s.date}-${s.meal}`}>
                <p className="text-[10px] font-black uppercase tracking-[0.14em] text-q-faint">{dayShort(s.date)} · {MEAL_LABEL[s.meal]}</p>
                <p className="mt-1 font-display text-lg font-extrabold text-q-text">{s.emoji} {s.name}</p>
                {s.why && <p className="text-xs text-q-muted">{s.why}</p>}
                {!s.in_box && <p className="mt-1 text-[11px] text-q-faint">New idea · {s.ingredients.map((i) => i.item).join(", ")}</p>}
                {!!s.from_pantry?.length && <p className="mt-1 text-[11px] font-semibold text-emerald-300" data-testid={`plan-pantry-${s.date}-${s.meal}`}>Uses from pantry: {s.from_pantry.join(", ")}</p>}
                {!!s.missing?.length && <p className="mt-0.5 text-[11px] font-semibold text-amber-300" data-testid={`plan-missing-${s.date}-${s.meal}`}>Missing: {s.missing.join(", ")}</p>}
                <div className="mt-2 flex gap-2">
                  <button className="q-btn q-btn-secondary flex-1" disabled={!!busy} onClick={() => accept([s])} data-testid={`plan-accept-${s.date}-${s.meal}`}><Check className="h-4 w-4" /> Accept</button>
                  <button className="q-btn q-btn-ghost flex-1" disabled={!!busy} onClick={() => retry(s)} data-testid={`plan-retry-${s.date}-${s.meal}`}>{busy === k(s) ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Try again</button>
                </div>
              </div>
            ))}
            <button className="q-btn q-btn-primary w-full" disabled={!!busy} onClick={() => accept(sugg)} data-testid="plan-accept-all-btn">{busy === "accept" && <Loader2 className="h-4 w-4 animate-spin" />} Accept all</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
